import { useParams, useNavigate } from '@tanstack/react-router';
import MarkdownEditor, { README_EDITOR_OPTIONS } from '_/common/components/MarkdownEditor';
import { DEFAULT_README, PROJECT_ENVIRONMENT_OPTIONS } from '_/constants/common';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useCreateProject, useProjects } from '_/hooks/useProjects';
import createSuggestedSlug from '_/utils/createSuggestedSlug';
import { checkIfNameExists } from '_/utils/validation/nameValidator';
import { validateSlug, checkIfSlugExists, invalidSlugErrorMessage } from '_/utils/validation/slugValidator';
import { Form, Input, Select, Button, Space, Alert, notification } from 'antd';
import React, { ReactElement, useState, useEffect } from 'react';

function NewProject(): ReactElement {
  const [form] = Form.useForm();
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{ orgSlug: string }>;
  const orgSlug = params.orgSlug || '';

  const { selectedOrganization } = useOrganization();
  const { setBreadcrumbs } = useTopHeader();
  const { data: projects = [] } = useProjects(orgSlug);
  const { mutateAsync: createProjectMutation, isPending: isCreating } = useCreateProject();

  const [projectReadme, setProjectReadme] = useState<string>(DEFAULT_README);
  const [saveProjectErrors, setSaveProjectErrors] = useState<any[]>([]);
  const [slugIsSame, setSlugIsSame] = useState<boolean>(true);

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: 'New Project' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [selectedOrganization, orgSlug, setBreadcrumbs]);

  const onFinishFailed = () => {};

  const onFinish = async (values: any) => {
    setSaveProjectErrors([]);

    if (!selectedOrganization) {
      console.error('No selectedOrganization available');
      setSaveProjectErrors([{ type: 'api', error: 'Organization not loaded. Please refresh the page.' }]);
      return;
    }

    // Client-side validation for uniqueness
    if (checkIfNameExists(projects, values.name)) {
      setSaveProjectErrors([
        {
          type: 'name',
          error: `A project with the name "${values.name}" already exists`,
        },
      ]);
      return;
    }

    if (checkIfSlugExists(projects, values.slug)) {
      setSaveProjectErrors([
        {
          type: 'slug',
          error: `A project with the id/slug "${values.slug}" already exists`,
        },
      ]);
      return;
    }

    try {
      const newProject = await createProjectMutation({
        project: {
          name: values.name,
          slug: values.slug,
          environment: values.environment,
        },
        projectReadme: projectReadme,
      });

      notification.success({
        message: 'Project Saved',
      });
      void history({ to: `/orgs/${orgSlug}/projects/${newProject.slug}` });
    } catch (error: any) {
      console.error('Error creating project:', error);
      setSaveProjectErrors(error?.messages || [{ type: 'api', error: error?.message || 'Unknown error' }]);
    }
  };

  const onCancel = () => {
    void history({ to: `/orgs/${orgSlug}` });
  };

  const onReadmeChange = (value: string) => {
    setProjectReadme(value);
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (slugIsSame) {
      form.setFieldsValue({
        name: name,
        slug: createSuggestedSlug(name),
      });
    }
  };

  const onSlugChange = () => {
    const name = form.getFieldValue('name');
    const slug = form.getFieldValue('slug');
    setSlugIsSame(slug === createSuggestedSlug(name));
  };

  // Filter errors by type
  const nameErrors = saveProjectErrors.filter((e: any) => e.type === 'name');
  const slugErrors = saveProjectErrors.filter((e: any) => e.type === 'slug');
  const apiErrors = saveProjectErrors.filter((e: any) => e.type === 'api');

  return (
    <>
      {apiErrors.length > 0 && (
        <Alert
          message="Error"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {apiErrors.map((error: any, idx: number) => (
                <li key={idx}>{error.error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          name: '',
          slug: '',
          environment: 'PRODUCTION',
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please input the project name!' }]}
          validateStatus={nameErrors.length > 0 ? 'error' : undefined}
          help={nameErrors.length > 0 ? nameErrors[0].error : undefined}
        >
          <Input placeholder="Project name" onChange={onNameChange} />
        </Form.Item>

        <Form.Item
          label="Slug / ID - CANNOT BE CHANGED AFTER CREATION"
          name="slug"
          rules={[
            { required: true, message: 'Please input the project slug!' },
            {
              validator: function (_, value) {
                if (!value) return Promise.resolve();
                if (!validateSlug(value)) {
                  return Promise.reject(new Error(invalidSlugErrorMessage));
                }
                return Promise.resolve();
              },
            },
          ]}
          validateStatus={slugErrors.length > 0 ? 'error' : undefined}
          help={slugErrors.length > 0 ? slugErrors[0].error : undefined}
        >
          <Input placeholder="project-slug" onChange={onSlugChange} />
        </Form.Item>

        <Form.Item label="Environment" name="environment" rules={[{ required: true, message: 'Please select an environment!' }]}>
          <Select placeholder="Select Environment">
            {PROJECT_ENVIRONMENT_OPTIONS.map((env: string) => (
              <Select.Option value={env} key={env}>
                {env}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ marginBottom: 55, marginTop: 25, position: 'relative' }}>
          <MarkdownEditor editorOptions={README_EDITOR_OPTIONS} onChange={onReadmeChange} value={projectReadme} />
          {!projectReadme && <div style={{ color: '#ff4d4f', fontSize: 14, marginTop: 4 }}>README is required</div>}
        </div>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isCreating}>
              Save
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </>
  );
}

export default NewProject;
